import cardValidator from "card-validator";

import type {ValidatorResult} from "../types";
import {BaseValidator} from "./validator.interface";

export class CardTypeValidator extends BaseValidator {
    private readonly allowedTypes: Array<string>;

    constructor(allowedTypes: Array<string>, message: string = "cardNotAccepted") {
        super(message);
        this.allowedTypes = allowedTypes;
    }

    validate(value: string): ValidatorResult {
        if (!value) return this.success();

        const cleanValue = value.replace(/\s/g, "");
        const result = cardValidator.number(cleanValue);

        if (!result.card?.type) return this.success();

        return this.allowedTypes.includes(result.card.type) ? this.success() : this.fail();
    }
}

export class CreditCardValidator extends BaseValidator {
    constructor(message: string = "invalidCreditCard") {
        super(message);
    }

    validate(value: string): ValidatorResult {
        const cleanValue = value.replace(/\s/g, ""); // Remove spaces
        const result = cardValidator.number(cleanValue);
        return result.isValid ? this.success() : this.fail();
    }
}

export class CVCValidator extends BaseValidator {
    constructor(message: string = "invalidCVC") {
        super(message);
    }

    validate(value: string): ValidatorResult {
        // Accept 3 or 4 digits (covers both regular cards and AmEx)
        if (!/^\d{3,4}$/.test(value)) {
            return this.fail();
        }

        // Additional validation using card-validator
        // Use the actual length of the value as maxLength
        const maxLength = value.length;
        const result = cardValidator.cvv(value, maxLength);
        return result.isValid ? this.success() : this.fail();
    }
}

export class ExpirationMonthValidator extends BaseValidator {
    constructor(message: string = "invalidExpirationMonth") {
        super(message);
    }

    validate(value: string): ValidatorResult {
        const monthNum = parseInt(value, 10);
        if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
            return this.fail();
        }
        return this.success();
    }
}

export class ExpirationYearValidator extends BaseValidator {
    constructor(message: string = "invalidExpirationYear") {
        super(message);
    }

    validate(value: string): ValidatorResult {
        const yearNum = parseInt(value, 10);
        const currentYear = new Date().getFullYear();

        if (isNaN(yearNum) || yearNum < currentYear) {
            return this.fail();
        }
        return this.success();
    }
}
